import React, { useMemo, useCallback, useState } from 'react';
import { StyleSheet, Pressable, View, ActivityIndicator, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useRouter } from 'expo-router';
import { nativeTaskResultsRoute } from '@/lib/localRuntime/nativeTaskOperations';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import TaskAvatar from '@/components/Tasks/TaskAvatar';
import TaskAttachment from '@/components/Tasks/TaskAttachment';
import TaskFavoriteHeart from '@/components/Tasks/TaskFavoriteHeart';
import Switch from '@/components/Inputs/Switch';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import { useModalStore, useInferenceTasksStore, useToastStore, useChatStore } from '@/store';

// Decode HTML entities
const decodeHtmlEntities = (text) => {
  if (!text) return text;
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
};

const createStyles = (colors, isXS, isSM, isXL, isAltBGColor) => StyleSheet.create({
  taskCard: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
    padding: isSM ? 12 : 14,
    borderRadius: 12,
    backgroundColor: isAltBGColor ? colors.solid_color_tertiary : colors.solid_color_block,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    // Responsive: 1 col (mobile) -> 2 col (sm) -> 3 col (md) -> 4 col (xl)
    width: (isXS || Platform.OS === 'macos') ? '100%' : isSM ? 'calc(50% - 6px)' : isXL ? 'calc(25% - 9px)' : 'calc(33.333% - 8px)',
    minHeight: isXS ? 100 : 120,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    overflow: 'hidden',
  },
  hoveredTaskCard: {
    backgroundColor: isAltBGColor ? colors.solid_color_secondary_dark : colors.solid_color_tertiary,
    borderColor: colors.border_color_primary,
  },
  taskHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  taskContent: {
    flex: 1,
    width: '100%',
  },
  taskName: {
    marginBottom: 2,
  },
  taskMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 'auto',
    flexWrap: 'wrap',
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  enabledDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
  },
  viewResultsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    backgroundColor: colors.solid_color_secondary,
  },
  viewResultsButtonHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  runNowButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    backgroundColor: colors.accent_color_primary,
  },
  runNowButtonHovered: {
    backgroundColor: colors.accent_color_primary_active,
  },
  runNowButtonDisabled: {
    opacity: 0.6,
  },
  actionButtonsLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
});

function formatTime(time) {
  if (!time) return '';
  if (typeof time === 'string') return time;
  const { hour, minute } = time;
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const h = hour % 12 || 12;
  const m = String(minute).padStart(2, '0');
  return `${h}:${m} ${suffix}`;
}

function formatSchedule(schedule, t) {
  if (!schedule || !schedule.type) return t('tasks.once');

  const type = schedule.type;
  const time = formatTime(schedule.timeOfDay);
  const dayOfWeek = Number.isInteger(schedule.daysOfWeek)
    ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][schedule.daysOfWeek]
    : schedule.dayOfWeek;

  if (type === 'daily') {
    return time ? `${t('tasks.daily')} ${time}` : t('tasks.daily');
  }

  if (type === 'weekly') {
    const day = dayOfWeek && typeof dayOfWeek === 'string'
      ? dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1, 3)
      : '';
    return day ? `${day} ${time}` : t('tasks.weekly');
  }

  if (type === 'monthly') {
    return time ? `${t('tasks.monthly')} ${time}` : t('tasks.monthly');
  }

  return type === 'once' ? t('tasks.once') : type.charAt(0).toUpperCase() + type.slice(1);
}

function TaskCard({ task, onPress, showActions = false }) {
  const { t } = useTranslation();
  const { colors, theme } = useTheme();
  const { isXS, isSM, isXL } = useResponsiveness();
  const router = useRouter();
  const { openModal } = useModalStore(state => state.actions);
  const toggleTask = useInferenceTasksStore(state => state.toggleTask);
  const runTaskNow = useInferenceTasksStore(state => state.runTaskNow);
  const fetchChats = useChatStore(state => state.fetchChats);
  const { addToast } = useToastStore(state => state.actions);
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  const styles = useMemo(() => createStyles(colors, isXS, isSM, isXL, isAltBGColor), [colors, isXS, isSM, isXL, isAltBGColor]);
  
  const [isToggling, setIsToggling] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  const enabled = task?.flags?.isEnabled ?? true;
  const scheduleLabel = formatSchedule(task?.schedule, t);
  const hasConversation = task?.nativeWork || task?.conversationId || task?.conversation?.id;

  const handlePress = () => {
    if (onPress) {
      onPress(task);
    } else {
      openModal('CreateTaskModal', { task });
    }
  };

  const handleToggle = useCallback(async (newValue) => {
    if (isToggling) return;
    
    try {
      setIsToggling(true);
      await toggleTask(task.id, newValue);
      addToast(newValue ? t('tasks.enabled') : t('tasks.paused'), 'success');
    } catch (error) {
      addToast(error.message || t('tasks.updateFailed'), 'error');
    } finally {
      setIsToggling(false);
    }
  }, [task.id, toggleTask, addToast, isToggling, t]);

  const handleRunNow = useCallback(async (e) => {
    e?.stopPropagation?.();
    if (isRunning) return;
    
    try {
      setIsRunning(true);
      const result = await runTaskNow(task.id);
      const conversationId = result?.conversationId;
      if (conversationId) {
        router.push(`/c/${conversationId}`);
      } else {
        addToast(result.waitingForMac ? 'Queued for this Mac.' : t('tasks.started'), 'success');
        if (!result.waitingForMac) setTimeout(() => fetchChats(1), 2000);
      }
    } catch (error) {
      addToast(error.message || t('tasks.runFailed'), 'error');
    } finally {
      setIsRunning(false);
    }
  }, [task.id, runTaskNow, addToast, isRunning, fetchChats, t, router]);

  const handleViewResults = useCallback((e) => {
    e?.stopPropagation?.();
    if (task?.nativeWork) {
      try { router.push(nativeTaskResultsRoute(task)); } catch (error) { addToast(error.message, 'error'); }
      return;
    }
    const conversationId = task?.conversationId || task?.conversation?.id;
    if (conversationId) {
      router.push(`/c/${conversationId}`);
    }
  }, [task, router, addToast]);

  return (
    <Pressable
      style={({ hovered }) => [
        styles.taskCard,
        hovered && styles.hoveredTaskCard,
      ]}
      onPress={handlePress}
    >
      <View style={styles.taskHeader}>
        <TaskAvatar task={task} size={isSM ? 40 : 44} />
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 2 }}>
          <View style={styles.statusBadge}>
            <View style={[
              styles.enabledDot,
              { backgroundColor: enabled ? colors.accent_color_primary : colors.text_color_tertiary }
            ]} />
            <Text size='xs' color={enabled ? 'primary' : 'secondary'}>
              {enabled ? t('tasks.enabledStatus') : t('tasks.pausedStatus')}
            </Text>
          </View>
          <TaskFavoriteHeart task={task} />
        </View>
      </View>

      <View style={styles.taskContent}>
        <Text
          size={isXS ? 'n' : 'm'}
          color='primary'
          weight='bold'
          style={styles.taskName}
          numberOfLines={2}
        >
          {task?.name || t('tasks.untitled')}
        </Text>
      </View>

      <TaskAttachment task={task} />

      <View style={styles.taskMeta}>
        <View style={styles.metaItem}>
          <Icon name="arrows-rotate" size={12} color={colors.text_color_secondary} />
          <Text size='xs' color='secondary'>{scheduleLabel}</Text>
        </View>
        {task?.emailDelivery?.isEnabled && (
          <View style={styles.metaItem}>
            <Icon name="envelope" size={12} color={colors.text_color_secondary} />
            <Text size='xs' color='secondary'>{t('tasks.email')}</Text>
          </View>
        )}
      </View>

      {/* Quick Actions Row */}
      {showActions && (
        <View style={styles.actionsRow}>
          <View style={styles.actionButtonsLeft}>
            <Pressable
              style={({ hovered }) => [
                styles.runNowButton,
                hovered && styles.runNowButtonHovered,
                isRunning && styles.runNowButtonDisabled,
              ]}
              onPress={handleRunNow}
              disabled={isRunning}
            >
              {isRunning ? (
                <ActivityIndicator size={10} color="#fff" />
              ) : (
                <Icon name="play" size={10} color="#fff" solid />
              )}
              <Text size='xs' style={{ color: '#fff' }}>{isRunning ? t('tasks.running') : t('tasks.runNow')}</Text>
            </Pressable>
            {hasConversation && (
              <Pressable
                style={({ hovered }) => [
                  styles.viewResultsButton,
                  hovered && styles.viewResultsButtonHovered,
                ]}
                onPress={handleViewResults}
              >
                <Icon name="arrow-up-right-from-square" size={10} color={colors.text_color_secondary} />
                <Text size='xs' color='secondary'>{t('tasks.results')}</Text>
              </Pressable>
            )}
          </View>
          <View onClick={(e) => e.stopPropagation()}>
            <Switch
              value={enabled}
              onValueChange={handleToggle}
              disabled={isToggling}
              accessibilityLabel={`${t('tasks.enabledStatus')}: ${task?.name || t('tasks.untitled')}`}
            />
          </View>
        </View>
      )}
    </Pressable>
  );
}

// Loading placeholder component
export function TaskCardPlaceholder() {
  const { colors, theme } = useTheme();
  const { isXS, isSM, isXL } = useResponsiveness();
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  const styles = useMemo(() => createStyles(colors, isXS, isSM, isXL, isAltBGColor), [colors, isXS, isSM, isXL, isAltBGColor]);

  return (
    <View style={styles.taskCard}>
      <View style={styles.taskHeader}>
        <View style={[styles.iconContainer, { opacity: 0.5 }]} />
      </View>
      <View style={styles.taskContent}>
        <View style={{ 
          height: 14, 
          width: '70%', 
          backgroundColor: colors.solid_color_secondary, 
          borderRadius: 4,
          marginBottom: 8,
        }} />
        <View style={{ 
          height: 10, 
          width: '40%', 
          backgroundColor: colors.solid_color_secondary, 
          borderRadius: 4,
        }} />
      </View>
    </View>
  );
}

export default TaskCard;
